import React from 'react';
import { Select, InputNumber } from 'antd';
import { F21A0, JUMP_TYPE_OPTIONS } from '../../../services/pw220aApi';
import LookupField from '../../../components/LookupField';

interface TabMainInfoProps {
  formData: F21A0;
  names: Record<string, Record<string, string>>;
  isEditing: boolean;
  getField: <K extends keyof F21A0>(key: K) => F21A0[K];
  setField: <K extends keyof F21A0>(key: K, value: F21A0[K]) => void;
  isApproved: boolean;
}

const TabMainInfo: React.FC<TabMainInfoProps> = React.memo(
  ({ formData, names, isEditing, getField, setField, isApproved }) => {
    const warehouseName = names['warehouse']?.[formData.f21a0_07] ?? '';
    const employeeName = names['employee']?.[formData.f21a0_12] ?? '';
    const deptName = names['dept']?.[formData.f21a0_10] ?? '';

    const dateVal = formData.f21a0_11
      ? formData.f21a0_11.substring(0, 10)
      : '';

    return (
      <div className="form-grid">
        {/* Row 1: 調整單號 + 日期 */}
        <label className="form-label">調整單號</label>
        <div className="form-field">
          <input
            className="form-input"
            value={getField('f21a0_01') as string}
            readOnly
            disabled
          />
        </div>
        <label className="form-label">日　　期</label>
        <div className="form-field">
          <input
            className="form-input"
            type="date"
            value={dateVal}
            onChange={(e) =>
              setField('f21a0_11', e.target.value ? e.target.value + 'T00:00:00Z' : null)
            }
            disabled={!isEditing}
          />
        </div>

        {/* Row 2: 庫號 + 庫號名稱 */}
        <label className="form-label">庫　　號</label>
        <div className="form-field lookup-field">
          <LookupField lookupType="warehouse" value={(getField('f21a0_07') as string) || ''}
            onChange={(code) => setField('f21a0_07', code)}
            disabled={!isEditing} title="庫號搜尋" />
          <span className="lookup-name">{warehouseName}</span>
        </div>
        <label className="form-label">跳號別</label>
        <div className="form-field">
          <Select
            value={getField('f21a0_31') as string}
            onChange={(val) => setField('f21a0_31', val)}
            disabled={!isEditing}
            style={{ width: '100%' }}
            options={JUMP_TYPE_OPTIONS}
          />
        </div>

        {/* Row 3: 人員代號 + 人員名稱 */}
        <label className="form-label">人員代號</label>
        <div className="form-field lookup-field">
          <LookupField lookupType="employee" value={(getField('f21a0_12') as string) || ''}
            onChange={(code) => setField('f21a0_12', code)}
            disabled={!isEditing} title="人員搜尋" />
          <span className="lookup-name">{employeeName}</span>
        </div>
        <label className="form-label">部門代號</label>
        <div className="form-field lookup-field">
          <LookupField lookupType="department" value={(getField('f21a0_10') as string) || ''}
            onChange={(code) => setField('f21a0_10', code)}
            disabled={!isEditing} title="部門搜尋" />
          <span className="lookup-name">{deptName}</span>
        </div>

        {/* Row 4: 說明 full width */}
        <label className="form-label">說　　明</label>
        <div className="form-field span-3">
          <input
            className="form-input"
            value={getField('f21a0_15') as string}
            maxLength={80}
            onChange={(e) => setField('f21a0_15', e.target.value)}
            disabled={!isEditing}
          />
        </div>

        {/* Row 5: 總金額 + 總項數 */}
        <label className="form-label">總金額</label>
        <div className="form-field">
          <InputNumber
            className="text-right"
            value={getField('f21a0_17') as number}
            readOnly
            disabled
            style={{ width: '100%' }}
            precision={2}
            formatter={value => value ? Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 }) : '0'}
          />
        </div>
        <label className="form-label">總項數</label>
        <div className="form-field">
          <input
            className="form-input"
            value={getField('f21a0_16') as number}
            readOnly
            disabled
          />
        </div>

        {/* Row 6: 調整類別 */}
        <label className="form-label">調整類別</label>
        <div className="form-field">
          <input
            className="form-input"
            value={getField('f21a0_18') as string}
            maxLength={4}
            onChange={(e) => setField('f21a0_18', e.target.value)}
            disabled={!isEditing}
          />
        </div>
        {isApproved && (
          <div className="form-field span-2 approved-badge">已核准</div>
        )}

        {/* Row 7: 備註 full width */}
        <label className="form-label">備　　註</label>
        <div className="form-field span-3">
          <textarea
            className="form-textarea"
            value={getField('f21a0_30') as string}
            rows={3}
            onChange={(e) => setField('f21a0_30', e.target.value)}
            readOnly={!isEditing}
          />
        </div>
      </div>
    );
  }
);

TabMainInfo.displayName = 'TabMainInfo';
export default TabMainInfo;
